import httpClient from '../httpClient';
import { AxiosRequestConfig } from 'axios';
import { getFileParams, uploadFileToUrl } from 'uploadFileUtils';
import {
  DataRoomNda,
  DataRoomNdaSigner,
  UploadNdaFile,
  UploadNdaFileWithoutCreate,
} from '../../models/DataRoomNDA';
import { DropzoneFile } from 'components/Dropzone/utils';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { Params } from 'react-router-dom';

function getDataRoomNdas(dataRoomId: string) {
  return httpClient.get<DataRoomNda[]>(
    `data-room-ndas/data-rooms/${dataRoomId}`,
  );
}

async function uploadNda(
  dataRoomId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<DataRoomNda | null> {
  let dataRoomNdaId;

  try {
    const fileParams = getFileParams('dataRoomNda', file);

    const { data } = await httpClient.post<UploadNdaFile>(
      `data-room-ndas/data-rooms/${dataRoomId}/upload-temporary-object`,
      fileParams,
    );

    const { presignedPost, dataRoomNda } = data;

    dataRoomNdaId = dataRoomNda?._id;

    await uploadFileToUrl(presignedPost, file, config);

    return dataRoomNda;
  } catch {
    if (dataRoomNdaId) deleteNda(dataRoomNdaId);

    return null;
  }
}

function deleteNda(dataRoomNdaId: string) {
  return httpClient.delete(`data-room-ndas/${dataRoomNdaId}`);
}

function linkEnvelopeTemplate(
  dataRoomNdaId: string,
  envelopeTemplateId: string,
  envelopeTemplateName: string,
) {
  return httpClient.post(
    `data-room-ndas/${dataRoomNdaId}/link-envelope-template`,
    {
      envelopeTemplateId,
      envelopeTemplateName,
    },
  );
}

function getNdaSigners(dataRoomId: string, params: PaginationParams & Params) {
  return httpClient.get<PaginatedData<DataRoomNdaSigner>>(
    `data-room-nda-signers/data-rooms/${dataRoomId}`,
    { params },
  );
}

function sendNdaReminders(signingId: string) {
  return httpClient.post(`data-room-nda-signings/${signingId}/send-reminder`);
}

async function uploadNdaWithoutCreate(
  dataRoomId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<{ uploadedFile: DropzoneFile; temporaryFileId: string } | null> {
  try {
    const fileParams = getFileParams('dataRoomNda', file);

    const { data } = await httpClient.post<UploadNdaFileWithoutCreate>(
      `data-room-ndas/data-rooms/${dataRoomId}/upload-temporary-object-without-create`,
      fileParams,
    );

    const { presignedPost, uploadedFile, temporaryFileId } = data;

    await uploadFileToUrl(presignedPost, file, config);

    return { uploadedFile, temporaryFileId };
  } catch {
    return { uploadedFile: null, temporaryFileId: null };
  }
}

function submitNdaUpload(dataRoomId: string, temporaryFileId: string) {
  return httpClient.post(
    `data-room-ndas/data-rooms/${dataRoomId}/submit-upload`,
    { temporaryFileId },
  );
}

function linkNdaWithDataRoom(
  dataRoomId: string,
  envelopeTemplateId: string,
  envelopeTemplateName: string,
) {
  return httpClient.post(
    `data-room-ndas/data-rooms/${dataRoomId}/link-envelope-template`,
    { envelopeTemplateId, envelopeTemplateName },
  );
}

export default {
  getDataRoomNdas,
  uploadNda,
  deleteNda,
  linkEnvelopeTemplate,
  getNdaSigners,
  sendNdaReminders,
  uploadNdaWithoutCreate,
  submitNdaUpload,
  linkNdaWithDataRoom,
};
